import React, { useEffect, useState, useRef } from "react";
import baseUrl from "../../../api/universalApi";
import styles from "./Analytics.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaDownload } from "react-icons/fa";
import CampaignHead from "../CampaignHead/CampaignHead";


const Overview = ({ campaignData }) => {
  // Define all hooks first
  // console.log(campaignData);
  const [graphData, setGraphData] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterMetric, setFilterMetric] = useState("views");
  const [creatorPerformance, setCreatorPerformance] = useState({});
  const authData = useSelector((state) => state.auth.authData?.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const scrollContainerRef = useRef();
  const [showAll, setShowAll] = useState(false);

  const toggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

  // Helper functions moved to top level
  const findPeakInInterval = (metrics) => {
    if (!metrics.length) return { count: 0, timestamp: null };
    return metrics.reduce((max, current) =>
      current.count > max.count ? current : max
    );
  };

  const getTimeInterval = (date) => {
    const hours = date.getHours();
    if (hours >= 5 && hours < 12) return "Morning (5 AM - 12 PM)";
    if (hours >= 12 && hours < 17) return "Afternoon (12 PM - 5 PM)";
    if (hours >= 17 && hours < 21) return "Evening (5 PM - 9 PM)";
    return "Night (9 PM - 5 AM)";
  };

  const findPeakInterval = (intervalPeaks, metric) => {
    let maxInterval = null;
    let maxCount = -1;

    Object.entries(intervalPeaks).forEach(([interval, metrics]) => {
      if (metrics[metric].count > maxCount) {
        maxCount = metrics[metric].count;
        maxInterval = interval;
      }
    });

    return {
      interval: maxInterval,
      count: maxCount,
      time: maxInterval
        ? new Date(
          intervalPeaks[maxInterval][metric].timestamp
        ).toLocaleString()
        : null,
    };
  };

  // Main analysis function
  const analyzeGraphData = (data) => {
    if (!data || data.length === 0) return null;

    // Sort data by timestamp
    const sortedData = [...data].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );

    // Group data by time intervals
    const timeIntervalData = sortedData.reduce((acc, entry) => {
      const date = new Date(entry.timestamp);
      const interval = getTimeInterval(date);

      if (!acc[interval]) {
        acc[interval] = {
          views: [],
          likes: [],
          comments: [],
          shares: [],
        };
      }

      acc[interval].views.push({
        count: entry.views,
        timestamp: entry.timestamp,
      });
      acc[interval].likes.push({
        count: entry.likes,
        timestamp: entry.timestamp,
      });
      acc[interval].comments.push({
        count: entry.comments,
        timestamp: entry.timestamp,
      });
      acc[interval].shares.push({
        count: entry.shares,
        timestamp: entry.timestamp,
      });

      return acc;
    }, {});

    // Analyze each interval to find peaks
    const intervalPeaks = {};
    Object.entries(timeIntervalData).forEach(([interval, metrics]) => {
      intervalPeaks[interval] = {
        views: findPeakInInterval(metrics.views),
        likes: findPeakInInterval(metrics.likes),
        comments: findPeakInInterval(metrics.comments),
        shares: findPeakInInterval(metrics.shares),
      };
    });

    return {
      peaks: {
        views: findPeakInterval(intervalPeaks, "views"),
        likes: findPeakInterval(intervalPeaks, "likes"),
        comments: findPeakInterval(intervalPeaks, "comments"),
        shares: findPeakInterval(intervalPeaks, "shares"),
      },
    };
  };

  // Calculate creator performance
  const calculateCreatorPerformance = (creators) => {
    const performances = {};

    creators.forEach((creator) => {
      const currentViews = creator?.currentReelData?.[0]?.views || 0;
      const avgViews = creator.avgViews || 0;
      if (avgViews > 0) {
        const performance =
          (Math.abs(currentViews - avgViews) / avgViews) * 100;
        performances[creator._id] = {
          percentage: performance,
          creatorName: creator.displayname,
        };
      }
    });

    return performances;
  };

  useEffect(() => {
    const fetchAllCreatorsData = async () => {
      if (!campaignData || !campaignData.length || !campaignData[0].creators) {
        setLoading(false);
        return;
      }

      const campaign = campaignData[0];

      try {
        const promises = campaign?.creators.map((creator) =>
          fetch(`${baseUrl}/campaign/campaignDataRefresh`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ influxCreatorName: creator.displayname }),
          }).then((res) => res.json())
        );

        const results = await Promise.all(promises);
       // console.log("results", results); // console.log("results", results);
        const allGraphData = results.flatMap(
          (result) => result.graphData || []
        );
        setGraphData(allGraphData);

        if (allGraphData.length > 0) {
          const newInsights = analyzeGraphData(allGraphData);
          setInsights(newInsights);
        }

        const performanceData = calculateCreatorPerformance(campaign.creators);
        setCreatorPerformance(performanceData);
      } catch (error) {
        console.error("Error fetching graph data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllCreatorsData();
  }, [campaignData]);

  // Return early if no data
  if (!campaignData || campaignData.length === 0) {
    return <div>No campaign data available</div>;
  }

  if (loading) {
    return (
      <div role="status" class="w-full animate-pulse">
        <div class="h-10 bg-gray-500 rounded-full dark:bg-gray-300 w-full mb-2"></div>
        <div class="h-10 bg-gray-500 rounded-full dark:bg-gray-300 w-full mb-2"></div>{" "}
        <div class="h-10 bg-gray-500 rounded-full dark:bg-gray-300 w-full mb-2"></div>{" "}
        <div class="h-10 bg-gray-500 rounded-full dark:bg-gray-300 w-full mb-2"></div>{" "}
        <div class="h-10 bg-gray-500 rounded-full dark:bg-gray-300 w-full mb-2"></div>{" "}
        <div class="h-10 bg-gray-500 rounded-full dark:bg-gray-300 w-full mb-2"></div>{" "}
        <span class="sr-only">Loading...</span>
      </div>
    );
  }

  const campaign = campaignData[0];
  // console.log(campaign)

  const calculateTotalCost = (creators) => {
    // console.log(creators)
    return creators.reduce(
      (total, creator) => total + (Number(creator.earnedAmount) || 0),
      0
    );
  };

  // console.log(typeof(totalCost))
  const totalMetrics = campaign?.creators.reduce(
    (acc, creator) => {
      const currentReel = creator.currentReelData?.[0] || {};
      const cost = creator.earnedAmount;
      return {
        likes: acc.likes + (currentReel.likes || 0),
        views: acc.views + (currentReel.views || 0),
        comments: acc.comments + (currentReel.comments || 0),
        cost: calculateTotalCost(campaign.creators),
      };
    },
    { likes: 0, views: 0, comments: 0, cost: 0 }
  );

  // console.log(totalMetrics)

  const totalACPV = (totalMetrics.cost / totalMetrics.views || 0).toFixed(2);

  const engagementRate =
    (
      ((totalMetrics.likes + totalMetrics.comments) / totalMetrics.views) *
      100
    ).toFixed(2) || 0;

  const getTopCreators = (metric) => {
    let creators = [...campaign.creators].map((creator) => ({
      ...creator,
      metrics: creator.currentReelData?.[0] || {
        views: 0,
        likes: 0,
        comments: 0,
      },
    }));

    if (metric === "performance") {
      creators = creators.map((creator) => ({
        ...creator,
        performance: creatorPerformance[creator._id],
      }));

      creators.sort((a, b) => {
        if (!a.performance || a.performance.percentage === 0) return 1;
        if (!b.performance || b.performance.percentage === 0) return -1;
        return b.performance.percentage - a.performance.percentage;
      });

      return creators.slice(0, 2);
    } else {
      return creators
        .sort((a, b) => (b.metrics[metric] || 0) - (a.metrics[metric] || 0))
        .slice(0, 2);
    }
  };

  const topCreators = getTopCreators(filterMetric);

  const handleCampaign = () => {
    navigate("/createcampaign");
  };


  return (
    <div className="w-full p-4" style={{ fontFamily: "Gilroy-Medium" }}>
      {/* Header */}
      <header className="flex flex-col p-2 sm:flex-row justify-center items-start sm:items-center sm:mb-2 justify-between ">


        <h1 className="text-2xl font-bold mb-2">
          Welcome {authData?.displayname},
        </h1>

        <div className="flex items-center gap-4 mb-2">
          {/* Create Campaign Button */}
          <button
            className="bg-[#ff006b] text-white px-4 py-2 rounded-full hover:bg-pink-600 transition"
            onClick={handleCampaign}
          >
            Create Campaign
          </button>



          {/* Download Button */}
          <button className="bg-gray-100 text-2xl text-gray-600 p-2 rounded-full hover:bg-gray-200 transition">
            <FaDownload />
          </button>
        </div>
      </header>



      <CampaignHead campaignData={campaign} />


      {/* Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 border-2 rounded-[15px]  ">
          <h3 className="text-sm text-gray-600 mb-2">Total Cost</h3>
          <p className="text-2xl font-bold text-black">
            ₹{totalMetrics.cost.toLocaleString()}
          </p>
        </div>
        <div className="bg-white p-6 border-2 rounded-[15px] ">
          <h3 className="text-sm text-gray-600 mb-2">Engagement Rate</h3>
          <p className="text-2xl font-bold text-black">{engagementRate}%</p>
        </div>
        <div className="bg-white p-6 border-2 rounded-[15px]  ">
          <h3 className="text-sm text-gray-600 mb-2">Total Views</h3>
          <p className="text-2xl font-bold text-black">
            {totalMetrics.views.toLocaleString()}
          </p>
        </div>

      </div>

      <div className="relative mb-8">
        <h2>Creators</h2>
      {/* Scrollable Container */}
      <div
        className={`flex gap-6  px-2 transition-all duration-300 ${
          showAll ? "flex-wrap" : "flex-wrap"
        }`}
        ref={scrollContainerRef}
      >
        {campaign?.creators?.length > 0 ? (
          campaign.creators.map((creator) => (
            <div
              key={creator._id}
              className="bg-white  p-6 border-2 rounded-[15px] mb-2  "
            >
              {/* Card Content */}
              <div className="flex gap-3 items-start mb-4">
                <div>
                  <img
                    src={creator.profile_image}
                    alt={creator.displayname.slice(0, 1)}
                    className="w-16 h-16 rounded-full border-3 border-[#ff007b]"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    {creator.displayname}
                  </h3>
                  <p className="text-xs text-gray-600">{creator.email}</p>
                </div>
                <div>
                  <span className="text-sm bg-[#ff007b] text-white px-3 py-1 rounded-full">
                    {creator?.status}
                  </span>
                </div>
              </div>

              <div className={styles.creatorMetrics}>
                <div className={styles.metric}>
                  <p className="font-semibold">Views</p>
                  <span className="font-bold">
                    {creator.currentReelData?.[0]?.views?.toLocaleString() || 0}
                  </span>
                </div>
                <div className={styles.metric}>
                  <p className="font-semibold">ACPV</p>
                  <span className="font-bold">{creator.ACPV || "-"}</span>
                </div>
              </div>

              <div className="flex gap-3 items-center mt-2">
                <button className="bg-[#ff007b] text-white px-4 py-2 rounded-xl hover:bg-pink-600 transition">
                  View Content
                </button>
                <button className="text-[#ff007b] px-4 py-2 rounded-xl border-2 hover:bg-gray-200 transition">
                  Message
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="min-w-[300px] max-w-[300px] bg-white p-6 border-2 rounded-[15px]">
            <p className="text-gray-600">No creators available for this campaign.</p>
          </div>
        )}
      </div>

      {/* Toggle Show More/Less Button */}
      <div className="flex justify-center mt-4">
        <button
          className="text-[#ff007b] text-xl hover:scale-105 transition"
          onClick={toggleShowAll}
        >
          {showAll ? "↑" : "↓"}
        </button>
      </div>
    </div>



      {/* top creators section */ }
  <div className={styles.topCreators}>
    <div className={styles.topCreatorsHeader}>
      <h2 className="font-semibold">Top Creators</h2>
      <div className={styles.filterControls}>
        <select
          value={filterMetric}
          onChange={(e) => setFilterMetric(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="views">By Views</option>
          <option value="likes">By Likes</option>
          <option value="comments">By Comments</option>
          <option value="performance">By Performance</option>
        </select>
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {topCreators.map((creator, index) => {
        const percentage = creatorPerformance[creator._id]?.percentage || 0;
        
        const cappedPercentage = Math.min(percentage, 100);
        return (
          <div
            key={creator._id}
            className="bg-white p-6 border-2 rounded-[15px] border-gray-200 "
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <img
                  src={creator.profile_image}
                  alt={creator.displayname.slice(0, 1)}
                  className="w-16 h-16 rounded-full border-3 border-[#ff007b]"
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {creator.displayname}
                </h3>
                <p className="text-xs text-gray-600">{creator.email}</p>
              </div>
              <span className="text-sm bg-[#ff007b] text-white px-3 py-1 rounded-full">
                Rank #{index + 1}
              </span>
            </div>

            <div className={styles.creatorMetrics}>
              <div
                className={
                  filterMetric === "views" ? "bg-gray-100 rounded-lg p-2" : ""
                }
              >
                <p>Views</p>
                <span className="font-bold">{creator.metrics.views?.toLocaleString() || 0}</span>
              </div>
              <div
                className={
                  filterMetric === "likes" ? styles.highlightedMetric : ""
                }
              >
                <p>Likes</p>
                <span className="font-bold">{creator.metrics.likes?.toLocaleString() || 0}</span>
              </div>
              <div
                className={
                  filterMetric === "comments" ? styles.highlightedMetric : ""
                }
              >
                <p>Comments</p>
                <span className="font-bold">{creator.metrics.comments?.toLocaleString() || 0}</span>
              </div>
            </div>

            <div
              className={
                filterMetric === "performance"
                  ? styles.highlightedMetric
                  : ""
              }
            >
              <p className="flex justify-between mb-0"><span>Performance</span>
                <span>
                  {percentage.toFixed(2)}%
                </span>
              </p>

              <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                <div
                  className="bg-[#ff007b] h-2.5 rounded-full"
                  style={{
                    width: `${cappedPercentage}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>

    </div >
  );
};

export default Overview;
